import React, {Fragment} from 'react';
import {Button, Popup, Collaboration} from 'components';
import {noop, flow, useNamedSelector, useToggle, redirect, modal, isEmpty, useSelector} from 'helpers';
import {goals, settings} from 'store';
import {SmartGoalModal} from './smart-goal-modal';
import {dialog} from 'helpers/dialog';

export function MenuHandle(props) {
  return (
    <Popup.Menu.Handle Menu={Menu} {...props} />
  );
}

export function Menu({id, Wrapper = Fragment}) {
  const {goal, milestones, detailed} = useNamedSelector('goals.details', {id, context: 'goals'});
  const {gptConnects} = useSelector(settings.settings);
  return Boolean(goal) && (
    <Wrapper>
      {goal.status !== 'Completed' && (
        <Popup.Menu.Item icon="openai" onClick={askChatGPT(goal, milestones, gptConnects)}>
          Ask ChatGPT
        </Popup.Menu.Item>
      )}
      <Popup.Menu.Item icon={goal.status == 'Completed' ? 'undo' : 'check'} onClick={() => goals.dispatch.toggle(goal)}>
        {goal.status == 'Completed' ? 'Uncomplete' : 'Complete'}
      </Popup.Menu.Item>
      <Popup.Menu.Item icon="visible" to={`/goals/${goal.id}/`} enabled={!detailed}>
        View
      </Popup.Menu.Item>
      <Popup.Menu.Item icon="pencil" to={`/goals/${goal.id}/edit/`}>
        Edit
      </Popup.Menu.Item>
      <Popup.Menu.Item icon="duplicate" onClick={flow(() => goals.dispatch.smartDuplicate(goal, DuplicateMenu), redirect)}>
        Duplicate
      </Popup.Menu.Item>
      <Popup.Menu.Item icon="chart" to={`/goals/track/${goal.id}/`}>
        Track
      </Popup.Menu.Item>
      <Popup.Menu.Item icon="hold" onClick={() => goals.dispatch.hold(goal)}>
        {goal.status == 'On Hold' ? 'Unhold' : 'Hold'}
      </Popup.Menu.Item>
      <Collaboration.MenuItem id={goal.id} context="goals" />
      {detailed && <Popup.Menu.Separator />}
      {detailed && <Popup.Menu.Item icon="plus" to="./milestones/add/">Milestone</Popup.Menu.Item>}
      {detailed && <Popup.Menu.Item icon="plus" to="./activities/add/">Activity</Popup.Menu.Item>}
      <Popup.Menu.Separator />
      <Popup.Menu.DeleteItem onConfirm={() => goals.dispatch.remove(goal)} />
    </Wrapper>
  );
}

function DuplicateMenu({id, popup}) {
  const {allActivities: activities, milestones} = useNamedSelector('goals.details', {id, context: 'goals'});
  const [includeMilestones, toggleMilestones] = useToggle(true);
  const [includeActivities, toggleActivities] = useToggle(true);
  return (
    <Popup.Menu onClick={noop}>
      <Popup.Menu.SubHeading>Duplicate with:</Popup.Menu.SubHeading>
      <Popup.Menu.Checkbox enabled={milestones.length > 0} checked={includeMilestones} onClick={toggleMilestones}>
        Milestones
      </Popup.Menu.Checkbox>
      <Popup.Menu.Checkbox enabled={activities.length > 0} checked={includeActivities} onClick={toggleActivities}>
        Activities
      </Popup.Menu.Checkbox>
      <Button
        className={['primary', 'filled']}
        onClick={flow(popup.close, () => goals.dispatch.duplicate({
          id,
          milestones: includeMilestones ? milestones : [],
          activities: includeActivities ? activities : [],
        }), redirect)}
        children="Confirm"
      />
    </Popup.Menu>
  );
}

const askChatGPT = (goal, milestones, gptConnects) => {
  if (!goal.isSmart) {
    return modal(SmartGoalModal, {
      minimal: true,
      name: goal.name,
      dismissible: false,
      onSuccess: ({goal: suggested}) => {
        goals.dispatch.upsert({
          ...suggested,
          id: goal.id,
          isSmart: 1
        });
        redirect(`/goals/${goal.id}/review/`);
      }
    });
  } else if (isEmpty(milestones)) {
    if (gptConnects) {
      return dialog({
        title: 'Action Plan',
        content: 'Would you like to use ChatGPT to create a detailed Action Plan for this goal?',
        confirm: {
          title: 'Yes, ask ChatGPT!',
          callback: () => redirect(`/goals/${goal.id}/review/`)
        }
      });
    }
    return modal(SmartGoalModal, {
      minimal: true,
      name: goal.name,
      content: 'Would you like to use ChatGPT to create a detailed Action Plan for this goal?'
    });
  } else {
    return modal(SmartGoalModal, {
      minimal: true,
      content: 'Would you like to use ChatGPT to create a detailed Action Plan for this goal? The original goal will remain unchanged.',
      name: goal.name,
      dismissible: false,
      onSuccess: ({goal: suggested}) => {
        const {duplicateId} = goals.dispatch.duplicate({id: goal.id});
        goals.dispatch.upsert({
          ...suggested,
          id: duplicateId,
          isSmart: 1
        });
        redirect(`/goals/${duplicateId}/review/`);
      }
    });
  }
}
